import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  AppraisalCycleView, AppraisalOutcomeView, AppraisalRatingView, AppraisalTemplateView,
  AppraisalView, CalibrationSessionView, CompetencyView, GoalCheckinView, GoalView, PipView,
  RatingScaleView,
} from '../types'

const CFG = '/v1/performance'
const APR = '/v1/appraisals'
const GOALS = '/v1/goals'
const CAL = '/v1/calibration'
const PIPS = '/v1/pips'

export function useRatingScales() {
  return useQuery({
    queryKey: ['performance', 'scales'],
    queryFn: async () => (await apiClient.get<RatingScaleView[]>(`${CFG}/scales`)).data,
  })
}

export function useCompetencies() {
  return useQuery({
    queryKey: ['performance', 'competencies'],
    queryFn: async () => (await apiClient.get<CompetencyView[]>(`${CFG}/competencies`)).data,
  })
}

export function useAppraisalTemplates() {
  return useQuery({
    queryKey: ['performance', 'templates'],
    queryFn: async () =>
      (await apiClient.get<AppraisalTemplateView[]>(`${CFG}/templates`)).data,
  })
}

export function usePerformanceConfigMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['performance'] })
  }

  return {
    saveScale: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<RatingScaleView>(`${CFG}/scales/${id}`, body)).data
          : (await apiClient.post<RatingScaleView>(`${CFG}/scales`, body)).data,
      onSuccess: invalidate,
    }),
    /** Retires it: no longer offered, still resolving everywhere it is already named. */
    retireScale: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<RatingScaleView>(`${CFG}/scales/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    addLevel: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<RatingScaleView>(`${CFG}/scales/${id}/levels`, body)).data,
      onSuccess: invalidate,
    }),
    setGapThreshold: useMutation({
      mutationFn: async ({ id, gapBelowLevel }: { id: string; gapBelowLevel: number | null }) =>
        (await apiClient.post<RatingScaleView>(
          `${CFG}/scales/${id}/gap-threshold`, { gapBelowLevel })).data,
      onSuccess: invalidate,
    }),
    saveCompetency: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<CompetencyView>(`${CFG}/competencies/${id}`, body)).data
          : (await apiClient.post<CompetencyView>(`${CFG}/competencies`, body)).data,
      onSuccess: invalidate,
    }),
    /** Retires it: no longer offered, still resolving everywhere it is already named. */
    retireCompetency: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<CompetencyView>(`${CFG}/competencies/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    saveTemplate: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<AppraisalTemplateView>(`${CFG}/templates/${id}`, body)).data
          : (await apiClient.post<AppraisalTemplateView>(`${CFG}/templates`, body)).data,
      onSuccess: invalidate,
    }),
    /** Retires it: no longer offered, still resolving everywhere it is already named. */
    retireTemplate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<AppraisalTemplateView>(`${CFG}/templates/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    addSection: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<AppraisalTemplateView>(
          `${CFG}/templates/${id}/sections`, body)).data,
      onSuccess: invalidate,
    }),
  }
}

export function useAppraisalCycles() {
  return useQuery({
    queryKey: ['performance', 'cycles'],
    queryFn: async () => (await apiClient.get<AppraisalCycleView[]>(`${APR}/cycles`)).data,
  })
}

export function useCycleAppraisals(cycleId: string | null) {
  return useQuery({
    queryKey: ['performance', 'cycles', cycleId, 'appraisals'],
    enabled: Boolean(cycleId),
    queryFn: async () =>
      (await apiClient.get<AppraisalView[]>(`${APR}/cycles/${cycleId}/appraisals`)).data,
  })
}

export function useAppraisal(id: string | undefined) {
  return useQuery({
    queryKey: ['performance', 'appraisals', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<AppraisalView>(`${APR}/${id}`)).data,
  })
}

export function useAppraisalMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['performance'] })
    // Completing an appraisal writes into training and compensation.
    void queryClient.invalidateQueries({ queryKey: ['training'] })
    void queryClient.invalidateQueries({ queryKey: ['compensation'] })
  }

  return {
    saveCycle: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<AppraisalCycleView>(`${APR}/cycles`, body)).data,
      onSuccess: invalidate,
    }),
    openCycle: useMutation({
      mutationFn: async ({ id, employeeIds }: { id: string; employeeIds?: string[] }) =>
        (await apiClient.post<AppraisalCycleView>(
          `${APR}/cycles/${id}/open`, { employeeIds })).data,
      onSuccess: invalidate,
    }),
    addReviewer: useMutation({
      mutationFn: async ({ id, reviewerEmployeeId, reviewType }: {
        id: string; reviewerEmployeeId: string; reviewType: string
      }) => (await apiClient.post<AppraisalView>(
        `${APR}/${id}/reviewers`, { reviewerEmployeeId, reviewType })).data,
      onSuccess: invalidate,
    }),
    rate: useMutation({
      mutationFn: async ({ reviewId, sectionId, levelId, comments }: {
        reviewId: string; sectionId: string; levelId?: string | null; comments?: string
      }) => (await apiClient.post<AppraisalRatingView>(
        `${APR}/reviews/${reviewId}/ratings`, { sectionId, levelId, comments })).data,
      onSuccess: invalidate,
    }),
    submitReview: useMutation({
      mutationFn: async ({ reviewId, actingEmployeeId }: {
        reviewId: string; actingEmployeeId?: string
      }) => (await apiClient.post<AppraisalView>(
        `${APR}/reviews/${reviewId}/submit`, { actingEmployeeId })).data,
      onSuccess: invalidate,
    }),
    complete: useMutation({
      mutationFn: async ({ id, summary }: { id: string; summary?: string }) =>
        (await apiClient.post<AppraisalOutcomeView>(`${APR}/${id}/complete`, { summary })).data,
      onSuccess: invalidate,
    }),
  }
}

export function useGoals() {
  return useQuery({
    queryKey: ['performance', 'goals'],
    queryFn: async () => (await apiClient.get<GoalView[]>(GOALS)).data,
  })
}

export function useGoalCheckins(goalId: string | null) {
  return useQuery({
    queryKey: ['performance', 'goals', goalId, 'checkins'],
    enabled: Boolean(goalId),
    queryFn: async () =>
      (await apiClient.get<GoalCheckinView[]>(`${GOALS}/${goalId}/checkins`)).data,
  })
}

export function useGoalMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['performance'] })
  }

  return {
    save: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<GoalView>(GOALS, body)).data,
      onSuccess: invalidate,
    }),
    checkIn: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<GoalCheckinView>(`${GOALS}/${id}/checkins`, body)).data,
      onSuccess: invalidate,
    }),
    close: useMutation({
      mutationFn: async ({ id, status, outcomeNote }: {
        id: string; status: string; outcomeNote?: string
      }) => (await apiClient.post<GoalView>(
        `${GOALS}/${id}/close`, { status, outcomeNote })).data,
      onSuccess: invalidate,
    }),
  }
}

export function useCalibrationSessions() {
  return useQuery({
    queryKey: ['performance', 'calibration'],
    queryFn: async () =>
      (await apiClient.get<CalibrationSessionView[]>(`${CAL}/sessions`)).data,
  })
}

export function useCalibrationMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['performance'] })
  }

  return {
    open: useMutation({
      mutationFn: async ({ cycleId, name }: { cycleId: string; name: string }) =>
        (await apiClient.post<CalibrationSessionView>(
          `${CAL}/sessions`, { cycleId, name })).data,
      onSuccess: invalidate,
    }),
    adjust: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<CalibrationSessionView>(
          `${CAL}/sessions/${id}/adjust`, body)).data,
      onSuccess: invalidate,
    }),
    complete: useMutation({
      mutationFn: async ({ id, notes }: { id: string; notes?: string }) =>
        (await apiClient.post<CalibrationSessionView>(
          `${CAL}/sessions/${id}/complete`, { notes })).data,
      onSuccess: invalidate,
    }),
  }
}

export function usePips() {
  return useQuery({
    queryKey: ['performance', 'pips'],
    queryFn: async () => (await apiClient.get<PipView[]>(PIPS)).data,
  })
}

export function usePipMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['performance'] })
  }

  return {
    raise: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<PipView>(PIPS, body)).data,
      onSuccess: invalidate,
    }),
    activate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<PipView>(`${PIPS}/${id}/activate`)).data,
      onSuccess: invalidate,
    }),
    close: useMutation({
      mutationFn: async ({ id, status, outcomeNote }: {
        id: string; status: string; outcomeNote?: string
      }) => (await apiClient.post<PipView>(`${PIPS}/${id}/close`, { status, outcomeNote })).data,
      onSuccess: invalidate,
    }),
  }
}
